/**
 * Inline Horizontal Bar Chart for AI Assistant
 *
 * Horizontal bar chart component for rendering inside chat messages.
 * Better for many categories or long labels. Uses Recharts.
 */

import React, { useRef, useCallback } from 'react';
import { Box, Typography, useTheme, IconButton, Tooltip as MuiTooltip, alpha } from '@mui/material';
import { PrintIcon } from "../../icons";
import {useChartPalette} from "../../theme/chartPalette";
import {
  BarChart as RechartsBar,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

/**
 * Print a specific element by opening a new window
 */
function printElement(element: HTMLElement, title: string) {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) return;

  const styles = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
      } catch {
        return '';
      }
    })
    .join('\n');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>${styles}</style>
        <style>
          body { padding: 20px; font-family: system-ui, -apple-system, sans-serif; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>${element.outerHTML}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}

export interface InlineHorizontalBarChartData {
  name: string;
  value: number;
}

export interface InlineHorizontalBarChartProps {
  title: string;
  data: InlineHorizontalBarChartData[];
}


export const InlineHorizontalBarChart: React.FC<InlineHorizontalBarChartProps> = ({
  title,
  data,
}) => {
  const theme = useTheme();
  const paleta = useChartPalette();
  const chartRef = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    if (chartRef.current) {
      printElement(chartRef.current, title);
    }
  }, [title]);

  const displayData = data.slice(0, 50);

  const chartData = displayData.map((item, index) => ({
    name: item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name,
    fullName: item.name,
    value: item.value,
    color: paleta.color(index),
  }));

  // Calculate height based on number of items
  const chartHeight = Math.max(150, displayData.length * 28 + 40);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1,
            borderRadius: 1,
            boxShadow: theme.shadows[2],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" fontWeight="medium">
            {d.fullName}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {d.value.toLocaleString()}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (displayData.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No hay datos para mostrar
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={chartRef}
      sx={{
        my: 1,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
      }}
    >
      <MuiTooltip title="Imprimir" arrow>
        <IconButton
          size="small"
          onClick={handlePrint}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${theme.palette.divider}`,
            padding: '2px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            zIndex: 1,
            '@media print': { display: 'none' },
          }}
        >
          <PrintIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </MuiTooltip>
      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
        {title}
      </Typography>

      <Box sx={{ width: '100%', height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBar
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
          >
            <XAxis type="number" tick={{ fontSize: 10 }} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 10 }}
              width={100}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </RechartsBar>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default InlineHorizontalBarChart;
